import { Badge, Box, Button, Group, Menu, Switch, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { DataTableColumn } from 'mantine-datatable';
import { useCallback, useEffect, useState } from 'react';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import CompanySectionLayout from 'src/company/CompanySectionLayout';
import { useCompanyAppendices } from '../../hooks/useCompanyAppendices';
import { useCreateCompanyAppendix } from '../../hooks/useCreateCompanyAppendix';
import { useBulkArchiveCompanyAppendices } from '../../hooks/useBulkArchiveCompanyAppendices';
import { useBulkDeleteCompanyAppendices } from '../../hooks/useBulkDeleteCompanyAppendices';
import { useOneDriveCompanyAppendixUpload } from '../../hooks/useOneDriveCompanyAppendixUpload';
import { CompanyAppendix } from '../../types';
import AppendicesEmptyState from './AppendicesEmptyState';
import AppendixRowActions from './AppendixRowActions';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import ArchiveAppendixModal from './ArchiveAppendixModal';
import { useSharepoint } from 'src/data-capture/file-management/drop-zone/useSharepoint';
import { useSharePointPicker } from 'src/data-capture/file-management/drop-zone/SharePointProvider';
import { PickedItem } from 'src/data-capture/file-management/drop-zone/SharePointButton';
import { formatFileSize } from 'src/utils/fileSizeFormatting';

export default function AppendicesListPage() {
  const { appendices, isLoading: isLoadingAppendices } = useCompanyAppendices();
  const { createCompanyAppendix, isLoading: isUploading } = useCreateCompanyAppendix();
  const { bulkArchiveCompanyAppendices, isLoading: isArchiving } =
    useBulkArchiveCompanyAppendices();
  const { bulkDeleteCompanyAppendices, isLoading: isDeleting } = useBulkDeleteCompanyAppendices();
  const { createLink } = useFileTemporaryLinkMutation();
  const { uploadOneDriveFiles, isUploading: isUploadingFromOneDrive } =
    useOneDriveCompanyAppendixUpload();
  const { pendingPickedItems, clearPendingPickedItems } = useSharePointPicker();

  const [showArchived, setShowArchived] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState<CompanyAppendix[]>([]);
  const [appendicesToArchive, setAppendicesToArchive] = useState<CompanyAppendix[]>([]);
  const [appendicesToDelete, setAppendicesToDelete] = useState<CompanyAppendix[]>([]);

  const handleFileDrop = async (files: File[]) => {
    for (const file of files) {
      try {
        await createCompanyAppendix(file);
      } catch (err) {
        notifyAndLogError(
          'Upload failed',
          `We were not able to upload ${file.name}. Please try again or contact support.`,
          err,
        );
      }
    }
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileDrop(Array.from(files));
    }
    event.target.value = '';
  };

  const handleOneDriveUpload = async (items: PickedItem[]) => {
    try {
      await uploadOneDriveFiles(items);
    } catch (err) {
      notifyAndLogError(
        'Upload failed',
        'We were not able to upload from SharePoint. Please try again or contact support.',
        err,
      );
    }
  };

  useEffect(() => {
    if (pendingPickedItems && pendingPickedItems.length > 0) {
      handleOneDriveUpload(pendingPickedItems);
      clearPendingPickedItems();
    }
  }, [pendingPickedItems, clearPendingPickedItems]);

  const { handleClick: handleSharePointClick } = useSharepoint({
    onPickedItems: handleOneDriveUpload,
    onAccountChange: () => {},
    switchAccountRequested: false,
    onSwitchAccountHandled: () => {},
    fileExtensions: ['.pdf', '.docx'],
  });

  const handleViewAppendix = async (appendix: CompanyAppendix) => {
    try {
      const link = await createLink(appendix.file.id);
      window.open(link.url, '_blank');
    } catch (err) {
      notifyAndLogError(
        'Error',
        'We were not able to open the appendix. Please try again or contact support.',
        err,
      );
    }
  };

  const handleArchiveConfirm = async () => {
    if (appendicesToArchive.length === 0) return;
    try {
      await bulkArchiveCompanyAppendices(
        appendicesToArchive.map(a => a.id),
        true,
      );
      setAppendicesToArchive([]);
      setSelectedRecords([]);
    } catch (err) {
      notifyAndLogError(
        'Archive failed',
        'We were not able to archive the appendices. Please try again or contact support.',
        err,
      );
    }
  };

  const handleUnarchive = async (items: CompanyAppendix[]) => {
    if (items.length === 0) return;
    try {
      await bulkArchiveCompanyAppendices(
        items.map(a => a.id),
        false,
      );
      setSelectedRecords([]);
    } catch (err) {
      notifyAndLogError(
        'Unarchive failed',
        'We were not able to unarchive the appendices. Please try again or contact support.',
        err,
      );
    }
  };

  const handleDeleteConfirm = async () => {
    if (appendicesToDelete.length === 0) return;
    try {
      await bulkDeleteCompanyAppendices(appendicesToDelete.map(a => a.id));
      setAppendicesToDelete([]);
      setSelectedRecords([]);
    } catch (err) {
      notifyAndLogError(
        'Delete failed',
        'We were not able to delete the appendices. Please try again or contact support.',
        err,
      );
    }
  };

  const filterPredicate = useCallback(
    (item: CompanyAppendix, search: string): boolean => {
      const matchesSearch = item.file.fileName.toLowerCase().includes(search.toLowerCase());
      const matchesArchiveFilter = showArchived || !item.isArchived;
      return matchesSearch && matchesArchiveFilter;
    },
    [showArchived],
  );

  const availableAppendices = appendices.filter(a => !a.isArchived);
  const allAppendicesArchived = appendices.length > 0 && availableAppendices.length === 0;

  const columns: DataTableColumn<CompanyAppendix>[] = [
    ...(showArchived
      ? [
          {
            accessor: 'isArchived',
            title: 'Status',
            width: 100,
            render: (record: CompanyAppendix) =>
              record.isArchived ? (
                <Badge size="xs" color="gray">
                  Archived
                </Badge>
              ) : null,
          },
        ]
      : []),
    {
      accessor: 'file.fileName',
      title: 'Name',
      render: record => (
        <Box>
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
            {record.file.fileName}
          </Text>
          <Text size="xs" c="gray.6">
            {formatFileSize(record.file.fileSize)}
          </Text>
        </Box>
      ),
    },
    {
      accessor: 'uploadedByName',
      title: 'Uploaded by',
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.uploadedByName ?? '-'}
        </Text>
      ),
    },
    {
      accessor: 'createdAt',
      title: 'Uploaded',
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {dayjs(record.createdAt).format('D MMM YYYY')}
        </Text>
      ),
    },
    {
      accessor: 'actions',
      title: '',
      render: record => (
        <AppendixRowActions
          appendix={record}
          onView={handleViewAppendix}
          onArchive={appendix => setAppendicesToArchive([appendix])}
          onUnarchive={appendix => handleUnarchive([appendix])}
          onDelete={appendix => setAppendicesToDelete([appendix])}
          isUpdating={isArchiving}
        />
      ),
    },
  ];

  const isUploadingAny = isUploading || isUploadingFromOneDrive;
  const isBulkPending = isArchiving || isDeleting;
  const allSelectedArchived =
    selectedRecords.length > 0 && selectedRecords.every(a => a.isArchived);
  const allSelectedUnarchived =
    selectedRecords.length > 0 && selectedRecords.every(a => !a.isArchived);

  const SelectionActions = (
    <Group gap="xs">
      <Text size="sm" c="dimmed">
        {selectedRecords.length} selected
      </Text>
      {allSelectedUnarchived && (
        <Button onClick={() => setAppendicesToArchive(selectedRecords)} disabled={isBulkPending}>
          Archive
        </Button>
      )}
      {allSelectedArchived && (
        <Button
          onClick={() => handleUnarchive(selectedRecords)}
          loading={isArchiving}
          disabled={isBulkPending}
        >
          Unarchive
        </Button>
      )}
      {allSelectedArchived && (
        <Button
          color="red"
          onClick={() => setAppendicesToDelete(selectedRecords)}
          disabled={isBulkPending}
        >
          Delete
        </Button>
      )}
      <Button variant="subtle" onClick={() => setSelectedRecords([])} disabled={isBulkPending}>
        Clear
      </Button>
    </Group>
  );

  const UploadMenu = (
    <Menu shadow="md">
      <Menu.Target>
        <Button variant="primary" loading={isUploadingAny}>
          Upload appendix
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item component="label" closeMenuOnClick={false} style={{ cursor: 'pointer' }}>
          Upload from your computer
          <input
            type="file"
            accept=".pdf,.docx"
            multiple
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
        </Menu.Item>
        <Menu.Item onClick={handleSharePointClick}>Upload from SharePoint</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  const HeaderRow = (
    <>
      <Switch
        label="Show archived"
        checked={showArchived}
        onChange={event => setShowArchived(event.currentTarget.checked)}
        size="sm"
      />
      {selectedRecords.length > 0 ? SelectionActions : UploadMenu}
    </>
  );

  if (!isLoadingAppendices && appendices.length === 0) {
    return (
      <AppendicesEmptyState
        variant="no-appendices"
        onFileDrop={handleFileDrop}
        onUpload={handleUpload}
        onSharePointClick={handleSharePointClick}
        isUploading={isUploading || isUploadingFromOneDrive}
      />
    );
  }

  if (!isLoadingAppendices && allAppendicesArchived && !showArchived) {
    return (
      <AppendicesEmptyState
        variant="all-archived"
        onFileDrop={handleFileDrop}
        onUpload={handleUpload}
        onSharePointClick={handleSharePointClick}
        onShowArchived={() => setShowArchived(true)}
        isUploading={isUploading || isUploadingFromOneDrive}
      />
    );
  }

  return (
    <>
      <CompanySectionLayout
        title="Appendices"
        titleProps={{ size: 'displayXs', fw: FONT_WEIGHTS.semibold }}
        headerMb="lg"
        paperProps={{ p: 'xl' }}
      >
        <SimpleTable<CompanyAppendix>
          resource="company-appendices"
          columns={columns}
          HeaderRow={HeaderRow}
          enableSearch
          searchPlaceholder="Search by name"
          filterPredicate={filterPredicate}
          paginationDependencies={showArchived}
          pageSize={10}
          mode="client"
          tableProps={{
            flat: true,
            selectedRecords,
            onSelectedRecordsChange: setSelectedRecords,
          }}
        />
      </CompanySectionLayout>

      <ArchiveAppendixModal
        opened={appendicesToArchive.length > 0}
        onClose={() => setAppendicesToArchive([])}
        appendices={appendicesToArchive}
        onConfirm={handleArchiveConfirm}
        isLoading={isArchiving}
      />

      <ConfirmDeleteModal
        opened={appendicesToDelete.length > 0}
        onClose={() => setAppendicesToDelete([])}
        onConfirm={handleDeleteConfirm}
        title={
          appendicesToDelete.length === 1
            ? 'Delete appendix'
            : `Delete ${appendicesToDelete.length} appendices`
        }
        message={
          appendicesToDelete.length === 1
            ? `Are you sure you want to delete ${appendicesToDelete[0]?.file.fileName}? This action cannot be undone.`
            : `Are you sure you want to delete these ${appendicesToDelete.length} appendices? This action cannot be undone.`
        }
        isLoading={isDeleting}
      />
    </>
  );
}
